import { ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/i18n/I18nProvider";

type Section = { h: string; body: ReactNode };

interface PolicySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: "privacy" | "terms";
}

const lastUpdated = { en: "May 30, 2026", es: "30 de mayo de 2026" };

const privacy = {
  en: {
    title: "Privacy Policy & GDPR",
    intro:
      "Your privacy matters. This policy explains what we collect, why we collect it, and the rights you have under GDPR, CCPA and similar regulations. By browsing our website or sharing data with us, you accept this policy and our Terms of Use of the Smart Canvas web ecosystem.",
    sections: [
      {
        h: "1. Who we are",
        body: "Smart Canvas Solutions, S.A. (\"Smart Canvas\", \"we\", \"our\") operates this website and the Smart Canvas OS platform. You can reach our Data Protection contact at privacy@smart-canvas.com.",
      },
      {
        h: "2. Data we collect",
        body: "Contact details you provide (name, email, company, message), basic analytics (pages visited, device, locale, anonymized IP), and any content you submit through forms, briefs or your account. We never collect special-category data unless you actively share it.",
      },
      {
        h: "3. Why we use it",
        body: "To reply to inquiries, deliver our services, improve the product, secure the platform, and meet legal obligations. We rely on consent, contract performance, and legitimate interest as our lawful bases under GDPR Art. 6.",
      },
      {
        h: "4. Sharing",
        body: "We share data only with vetted processors that help us operate (hosting, email, analytics, payments). All processors sign DPAs and are bound to GDPR-equivalent standards. We never sell your data.",
      },
      {
        h: "5. International transfers",
        body: "Data may be processed in the EU, US, UK and other regions where our infrastructure runs. Transfers outside the EEA use Standard Contractual Clauses and additional safeguards.",
      },
      {
        h: "6. Retention",
        body: "We keep personal data only as long as needed for the purpose collected, or as required by law. Marketing data is removed on request. Account data is deleted within 30 days of account closure.",
      },
      {
        h: "7. Your rights (GDPR / CCPA)",
        body: "You can access, rectify, delete, restrict or port your data, withdraw consent, object to processing, and lodge a complaint with your supervisory authority. Write to privacy@smart-canvas.com — we respond within 30 days.",
      },
      {
        h: "8. Cookies",
        body: "We use essential cookies for the site to work and minimal analytics cookies to understand usage. You can disable non-essential cookies in your browser at any time.",
      },
      {
        h: "9. Security",
        body: "We apply encryption in transit and at rest, least-privilege access, audit logging and incident response procedures aligned with ISO 27001 best practices.",
      },
      {
        h: "10. Changes",
        body: "We may update this policy as the product evolves. Material changes will be announced on this page with a new \"last updated\" date.",
      },
    ] satisfies Section[],
  },
  es: {
    title: "Política de Privacidad y GDPR",
    intro:
      "Tu privacidad importa. Esta política explica qué recopilamos, por qué lo hacemos y los derechos que tienes bajo el GDPR, la CCPA y normativas similares. Al navegar nuestro sitio o compartir datos con nosotros, aceptas esta política y nuestros Términos de Uso del ecosistema web de Smart Canvas.",
    sections: [
      {
        h: "1. Quiénes somos",
        body: "Smart Canvas Solutions, S.A. (\"Smart Canvas\", \"nosotros\") opera este sitio web y la plataforma Smart Canvas OS. Puedes contactar a nuestro responsable de protección de datos en privacy@smart-canvas.com.",
      },
      {
        h: "2. Datos que recopilamos",
        body: "Datos de contacto que nos proporcionas (nombre, correo, empresa, mensaje), analíticas básicas (páginas visitadas, dispositivo, idioma, IP anonimizada) y cualquier contenido que envíes mediante formularios, briefs o tu cuenta. Nunca recopilamos datos de categorías especiales salvo que tú los compartas activamente.",
      },
      {
        h: "3. Para qué los usamos",
        body: "Para responder consultas, prestar nuestros servicios, mejorar el producto, asegurar la plataforma y cumplir obligaciones legales. Nos basamos en el consentimiento, la ejecución contractual y el interés legítimo como bases legales según el Art. 6 del GDPR.",
      },
      {
        h: "4. Compartición",
        body: "Compartimos datos solo con proveedores cuidadosamente seleccionados que nos ayudan a operar (hosting, correo, analítica, pagos). Todos firman DPAs y se rigen por estándares equivalentes al GDPR. Nunca vendemos tus datos.",
      },
      {
        h: "5. Transferencias internacionales",
        body: "Los datos pueden procesarse en la UE, EE.UU., Reino Unido y otras regiones donde corre nuestra infraestructura. Las transferencias fuera del EEE usan Cláusulas Contractuales Tipo y salvaguardas adicionales.",
      },
      {
        h: "6. Retención",
        body: "Conservamos los datos personales solo el tiempo necesario para el propósito recopilado o el exigido por ley. Los datos de marketing se eliminan a petición. Los datos de cuenta se eliminan dentro de los 30 días posteriores al cierre.",
      },
      {
        h: "7. Tus derechos (GDPR / CCPA)",
        body: "Puedes acceder, rectificar, eliminar, restringir o portar tus datos, retirar el consentimiento, oponerte al tratamiento y presentar una reclamación ante tu autoridad de control. Escríbenos a privacy@smart-canvas.com — respondemos en un plazo de 30 días.",
      },
      {
        h: "8. Cookies",
        body: "Usamos cookies esenciales para el funcionamiento del sitio y analíticas mínimas para entender el uso. Puedes desactivar las cookies no esenciales en tu navegador en cualquier momento.",
      },
      {
        h: "9. Seguridad",
        body: "Aplicamos cifrado en tránsito y en reposo, acceso de mínimo privilegio, registros de auditoría y procedimientos de respuesta ante incidentes alineados con las buenas prácticas de ISO 27001.",
      },
      {
        h: "10. Cambios",
        body: "Podemos actualizar esta política conforme evoluciona el producto. Los cambios relevantes se anunciarán en esta página con una nueva fecha de \"última actualización\".",
      },
    ] satisfies Section[],
  },
};

const terms = {
  en: {
    title: "Terms of Use",
    intro:
      "These Terms govern your use of the Smart Canvas website and web ecosystem. By browsing the site, submitting forms, or providing any data, you agree to these Terms and to our Privacy Policy.",
    sections: [
      {
        h: "1. Acceptance",
        body: "Accessing or using any part of our web ecosystem means you accept these Terms in full. If you do not agree, please discontinue use.",
      },
      {
        h: "2. Use of the site",
        body: "You may use the site for lawful, informational and business purposes. You agree not to attempt to disrupt, reverse engineer, scrape at scale, or misuse the platform.",
      },
      {
        h: "3. Intellectual property",
        body: "All content, branding, code, designs and trademarks are owned by Smart Canvas or its licensors and protected by international IP laws. No license is granted by implication.",
      },
      {
        h: "4. Submitted content",
        body: "Information you submit (briefs, messages, files) is treated under our Privacy Policy. You confirm you have the right to share it and grant us a limited license to use it to respond and deliver our services.",
      },
      {
        h: "5. Services & quotes",
        body: "Any proposal, quote or project agreement is governed by a separate signed contract. The website itself is informational and does not constitute a binding offer.",
      },
      {
        h: "6. Third-party links",
        body: "We may link to third-party sites. We are not responsible for their content or policies.",
      },
      {
        h: "7. Disclaimers",
        body: "The site is provided \"as is\" without warranties of any kind. We make reasonable efforts to keep it secure and available, but do not guarantee uninterrupted access.",
      },
      {
        h: "8. Limitation of liability",
        body: "To the maximum extent permitted by law, Smart Canvas is not liable for indirect, incidental or consequential damages arising from your use of the site.",
      },
      {
        h: "9. Governing law",
        body: "These Terms are governed by the laws of the Republic of Guatemala, without prejudice to mandatory consumer protections in your country of residence.",
      },
      {
        h: "10. Contact",
        body: "Questions about these Terms? Write to legal@smart-canvas.com.",
      },
    ] satisfies Section[],
  },
  es: {
    title: "Términos de Uso",
    intro:
      "Estos Términos regulan el uso del sitio web y el ecosistema web de Smart Canvas. Al navegar el sitio, enviar formularios o proporcionar cualquier dato, aceptas estos Términos y nuestra Política de Privacidad.",
    sections: [
      {
        h: "1. Aceptación",
        body: "Acceder o usar cualquier parte de nuestro ecosistema web implica que aceptas estos Términos en su totalidad. Si no estás de acuerdo, por favor deja de utilizarlo.",
      },
      {
        h: "2. Uso del sitio",
        body: "Puedes usar el sitio con fines legales, informativos y de negocio. Te comprometes a no intentar interrumpir, hacer ingeniería inversa, scraping masivo ni usos indebidos de la plataforma.",
      },
      {
        h: "3. Propiedad intelectual",
        body: "Todo el contenido, marca, código, diseños y signos distintivos son propiedad de Smart Canvas o sus licenciantes y están protegidos por las leyes internacionales de PI. No se otorga ninguna licencia de manera implícita.",
      },
      {
        h: "4. Contenido enviado",
        body: "La información que envías (briefs, mensajes, archivos) se trata según nuestra Política de Privacidad. Confirmas que tienes derecho a compartirla y nos otorgas una licencia limitada para usarla con el fin de responder y prestar nuestros servicios.",
      },
      {
        h: "5. Servicios y cotizaciones",
        body: "Cualquier propuesta, cotización o acuerdo de proyecto se rige por un contrato firmado por separado. El sitio en sí es informativo y no constituye una oferta vinculante.",
      },
      {
        h: "6. Enlaces a terceros",
        body: "Podemos enlazar a sitios de terceros. No somos responsables de su contenido ni de sus políticas.",
      },
      {
        h: "7. Exenciones",
        body: "El sitio se proporciona \"tal cual\" sin garantías de ningún tipo. Hacemos esfuerzos razonables por mantenerlo seguro y disponible, pero no garantizamos acceso ininterrumpido.",
      },
      {
        h: "8. Limitación de responsabilidad",
        body: "En la máxima medida permitida por la ley, Smart Canvas no es responsable por daños indirectos, incidentales o consecuentes derivados del uso del sitio.",
      },
      {
        h: "9. Ley aplicable",
        body: "Estos Términos se rigen por las leyes de la República de Guatemala, sin perjuicio de las protecciones obligatorias al consumidor en tu país de residencia.",
      },
      {
        h: "10. Contacto",
        body: "¿Dudas sobre estos Términos? Escríbenos a legal@smart-canvas.com.",
      },
    ] satisfies Section[],
  },
};

export function PolicySheet({ open, onOpenChange, kind }: PolicySheetProps) {
  const { lang } = useI18n();
  const data = kind === "privacy" ? privacy[lang] : terms[lang];
  const updatedLabel = lang === "es" ? "Última actualización" : "Last updated";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b hairline">
          <SheetTitle className="text-2xl tracking-tight">{data.title}</SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {updatedLabel}: {lastUpdated[lang]}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <div className="px-6 py-6 space-y-6">
            <p className="text-sm text-muted-foreground leading-relaxed">{data.intro}</p>
            <div className="space-y-5">
              {data.sections.map((s) => (
                <section key={s.h} className="space-y-2">
                  <h3 className="text-sm font-semibold tracking-tight">{s.h}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </section>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/70 pt-4 border-t hairline">
              © {new Date().getFullYear()} Smart Canvas Solutions, S.A.
            </p>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}